import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Animated, Text, View, TextInput, ScrollView, ImageBackground, Modal, Button, Pressable, Switch, Image, TouchableOpacity, Alert, Dimensions, StyleSheet, Platform } from "react-native";
import { getFirestore, doc, setDoc, updateDoc } from "firebase/firestore";
import { getAuth, signInAnonymously, createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, signOut, updateEmail, updatePassword } from "firebase/auth";
import { getDoc, collection, onSnapshot } from "firebase/firestore";
import { initializeApp } from "firebase/app";
import { firebaseConfig, app } from "../firebase";
import { useNavigation } from "@react-navigation/native";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"; // Import Firebase Storage
import { useUser } from "./UserContext";
import Popup from "./Popup";
import PromptSignUp from "./PromptSignUp";
import Friends from "./Friends";
import friendsIcon from "../assets/friends.png";
import friendsNotificationIcon from "../assets/friends-notification.png"; // Added import for friendsNotificationIcon
import settingsIcon from "../assets/settings.png"; // Import settings icon
import { FontAwesome5 } from '@expo/vector-icons';
import { useTutorial, TutorialPopup } from '../Screens/Helpers/Tutorials'; // Import the tutorial context
import { spotlightTarget, SPOTLIGHT_TARGETS } from './SpotlightTutorial/spotlightTargets';
import { useEmitSpotlightEventWhenOpened } from './SpotlightTutorial/spotlightBus';
import { SPOTLIGHT_EVENTS } from './SpotlightTutorial/spotlightEventNames';

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width < 1440;
const isLargeDesktop = width >= 1440;


// Initialize Firestore and other Firebase services
const db = getFirestore(app);
const storage = getStorage(app); // Initialize Firebase Storage

const fontFamilies = {
	Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

// Custom hook for scroll-based hide/show functionality
const useScrollHide = (options = {}) => {
	const { useWindowScroll = false } = options;
	const [scrollY] = useState(new Animated.Value(0));
	const lastScrollY = useRef(0);
	const [headerTranslateY] = useState(new Animated.Value(0));
	const isHeaderVisible = useRef(true);
	const animationRef = useRef(null);

	const handleScroll = (event) => {
		const currentScrollY = event.nativeEvent.contentOffset.y;
		const lastScrollYValue = lastScrollY.current;

		// Determine scroll direction
		const scrollingDown = currentScrollY > lastScrollYValue;
		const scrollingUp = currentScrollY < lastScrollYValue;

		// Update last scroll position
		lastScrollY.current = currentScrollY;

		if (Math.abs(currentScrollY - lastScrollYValue) > 10) {
			if (scrollingDown && currentScrollY > 100) {
				if (isHeaderVisible.current) {
					isHeaderVisible.current = false;
					if (animationRef.current) animationRef.current.stop();
					animationRef.current = Animated.timing(headerTranslateY, {
						toValue: -100,
						duration: 300,
						useNativeDriver: true,
					}).start();
				}
			} else if (scrollingUp) {
				if (!isHeaderVisible.current) {
					isHeaderVisible.current = true;
					if (animationRef.current) animationRef.current.stop();
					animationRef.current = Animated.timing(headerTranslateY, {
						toValue: 0,
						duration: 300,
						useNativeDriver: true,
					}).start();
				}
			}
		}
	};

	useEffect(() => {
		if (useWindowScroll && Platform.OS === 'web') {
			const handleWindowScroll = () => {
				const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
				handleScroll({ nativeEvent: { contentOffset: { y: currentScrollY } } });
			};
			window.addEventListener('scroll', handleWindowScroll);
			return () => window.removeEventListener('scroll', handleWindowScroll);
		}
	}, [useWindowScroll]);

	const onScroll = handleScroll;

	if (useWindowScroll) {
		return { headerTranslateY };
	} else {
		return { headerTranslateY, onScroll };
	}
};

// Add this function near the top with other functions
const initializeTutorialSetting = async (userEmail) => {
	try {
		const userDoc = doc(db, "users", userEmail);
		const userDocSnap = await getDoc(userDoc);
		
		if (userDocSnap.exists()) {
			const userData = userDocSnap.data();
			// If showTutorial is undefined, set it to true (default)
			if (userData.showTutorial === undefined) {
				await updateDoc(userDoc, { showTutorial: true });
				return true;
			}
			return userData.showTutorial;
		} else {
			// If user document doesn't exist, create it with showTutorial set to true
			await setDoc(userDoc, { showTutorial: true }, { merge: true });
			return true;
		}
	} catch (error) {
		
		return true; // Default to true if there's an error
	}
};

const HeaderBar = ({ isFixed = true, headerTranslateY: propHeaderTranslateY, isHubPage = false, isModalOpen = false }) => {
	const { currentUser, currentUserData } = useUser();
	const navigation = useNavigation();
	const auth = getAuth(app);
	const [showPromptSignUp, setShowPromptSignUp] = useState(false);
	const [showFriends, setShowFriends] = useState(false);
	const [showLogoutModal, setShowLogoutModal] = React.useState(false);
	const [friendRequests, setFriendRequests] = useState([]); // Added state for friendRequests
	const [showSettingsModal, setShowSettingsModal] = useState(false); // State for settings modal
	const [isPrivacyPublic, setIsPrivacyPublic] = useState(false); // State for privacy toggle
	useEmitSpotlightEventWhenOpened(showFriends, SPOTLIGHT_EVENTS.HEADER_FRIENDS_OPENED);
	useEmitSpotlightEventWhenOpened(showSettingsModal, SPOTLIGHT_EVENTS.HEADER_SETTINGS_OPENED);
	const [showPrivacyWarning, setShowPrivacyWarning] = useState(false); // State for privacy warning
	const friendsRef = useRef(null); // Ref for Friends component
	const { closeTutorial, showTutorial, setShowTutorial, closedTutorials, isLoading, tutorialsToShow, setActivePopupId, handleHover } = useTutorial(); // Use the tutorial context
	const [isTutorialEnabled, setIsTutorialEnabled] = useState(false);
	const [showSideMenu, setShowSideMenu] = useState(false);

	// Admin email whitelist - these emails can access admin even without accountType === "admin"
	const ADMIN_EMAIL_WHITELIST = [
		'trevorhawkins@dsignable.com',
		'info@mytassel.com'
	];

	// Check if user has admin access (either by accountType or email whitelist)
	const hasAdminAccess = useMemo(() => {
		if (!currentUserData) return false;
		const email = currentUserData.email?.toLowerCase();
		const isAdmin = currentUserData.accountType === "admin" || ADMIN_EMAIL_WHITELIST.includes(email);
		return isAdmin;
	}, [currentUserData]);

	const handleAdminPress = useCallback(() => {
		navigation.navigate('Admin');
	}, [navigation]);

	// Use the scroll hide hook
	const scrollHideResult = useScrollHide({ useWindowScroll: propHeaderTranslateY === undefined && Platform.OS === 'web' });
	const internalTranslateY = scrollHideResult.headerTranslateY;
	const onScroll = scrollHideResult.onScroll;

	// Hover states for buttons
	const [hoveredButton, setHoveredButton] = useState(null);
	const [hoveredIcon, setHoveredIcon] = useState(null);

	const headerBarStyle = {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		position: isFixed ? "fixed" : "relative",
		top: isFixed ? 0 : 'auto',
		left: isFixed ? 0 : 'auto',
		right: isFixed ? 0 : 'auto',
		width: '100%',
		zIndex: isFixed ? (isModalOpen ? -1 : 2000) : 'auto',
		pointerEvents: isModalOpen ? 'none' : 'auto',
		paddingHorizontal: isMobile ? 20 : isTablet ? 30 : isSmallDesktop ? 40 : 60,
		paddingTop: isMobile ? 30 : isTablet ? 40 : 50,
		paddingBottom: isMobile ? 8 : 12,
		boxSizing: 'border-box',
	};

	useEffect(() => {
		if (!auth.currentUser?.email) return;

		// 
		const userDoc = doc(db, "users", auth.currentUser.email);

		const unsubscribe = onSnapshot(userDoc, (docSnapshot) => {
			if (docSnapshot.exists()) {
				const userData = docSnapshot.data();
				const requests = userData.friends?.requests || {};
				// 
				setFriendRequests(Object.keys(requests));
			}
		});

		return () => unsubscribe();
	}, [auth.currentUser]);

	const friendsIconToShow = friendRequests.length > 0 ? friendsNotificationIcon : friendsIcon; // Conditional rendering of friendsIcon

	// Handle Logout
	const handleLogout = async () => {
		try {
			// Sign out from Firebase Auth
			await signOut(auth);


			// Navigate to the welcome or landing screen
			navigation.navigate("My Tassel - Welcome");
		} catch (error) {
			
		}
	};

	// Function to toggle between Login and Logout button based on user state
	const renderLoginOrLogoutButton = () => {
		if (currentUser || currentUserData) {
			return (
				<Pressable 
					{...spotlightTarget(SPOTLIGHT_TARGETS.HEADER_LOGOUT)}
					style={[
						styles.loginButton,
						hoveredButton === 'logout' && styles.loginButtonHover
					]} 
					onMouseEnter={() => setHoveredButton('logout')}
					onMouseLeave={() => setHoveredButton(null)}
					onPress={() => setShowLogoutModal(true)}
				>
					<Text style={styles.loginButtonText}>Logout</Text>
				</Pressable>
			);
		} else {
			return (
				<Pressable 
					style={[
						styles.loginButton,
						hoveredButton === 'login' && styles.loginButtonHover
					]} 
					onMouseEnter={() => setHoveredButton('login')}
					onMouseLeave={() => setHoveredButton(null)}
					onPress={() => navigation.navigate("My Tassel - Welcome", { popupType: "login" })}
				>
					<Text style={styles.loginButtonText}>Login</Text>
				</Pressable>
			);
		}
	};

	// Handle Link Press
	const handleLinkPress = (pageName) => {
		// 
		navigation.navigate(pageName); // Use the navigate function to change the page.
	};

	const generateUIC = () => {
		return Math.random().toString(36).substr(2, 9); // Simple UIC generation logic
	};

	const [hasInitializedPrivacy, setHasInitializedPrivacy] = useState(false);

	useEffect(() => {
		const updatePrivacyInFirestore = async () => {
			try {
				if (!currentUserData?.email) return;
				
				// Skip the initial fetch/sync
				if (!hasInitializedPrivacy) return;

				// Prevent changing G06785 demo account privacy
				if (currentUserData.uid === "G06785") {
					
					return;
				}

				const userDoc = doc(db, "users", currentUserData.email);
				
				const updates = {
					isFriendsListPrivate: !isPrivacyPublic
				};

				// Only generate new UIC when switching from public to private
				if (!isPrivacyPublic && !currentUserData.uic) {
					updates.uic = generateUIC();
				}

				await updateDoc(userDoc, updates);
				

				if (friendsRef.current) {
					friendsRef.current.setPrivacy(!isPrivacyPublic);
				}
			} catch (error) {
				
			}
		};

		// Only run the update if we have user data
		if (currentUserData?.email && hasInitializedPrivacy) {
			updatePrivacyInFirestore();
		}
	}, [isPrivacyPublic]); // Remove currentUserData from dependencies

	// Handle Privacy Toggle
	const handlePrivacyToggle = () => {
		if (!isPrivacyPublic) {
			setShowPrivacyWarning(true);
		} else {
			setIsPrivacyPublic(false);
		}
	};

	// Confirm Privacy Toggle
	const confirmPrivacyToggle = () => {
		setIsPrivacyPublic(true);
		setShowPrivacyWarning(false);
	};

	const fetchPrivacySetting = async () => {
		if (!currentUserData?.email) return;

		const userDoc = await getDoc(doc(db, "users", currentUserData.email));
		if (userDoc.exists()) {
			const userData = userDoc.data();
			// Only update if the field exists
			if (userData.isFriendsListPrivate !== undefined) {
				setIsPrivacyPublic(!userData.isFriendsListPrivate);
			}
			// Don't set a default if the field doesn't exist
		}
		// Mark as initialized after fetching
		setHasInitializedPrivacy(true);
	};

	useEffect(() => {
		fetchPrivacySetting();
	}, [currentUserData]);

	// Function to update tutorial preference in Firestore
	const updateTutorialPreference = async (showTutorial) => {
		if (!currentUserData || !currentUserData.email) return;
		const userDoc = doc(db, "users", currentUserData.email);
		await updateDoc(userDoc, { showTutorial });
	};

	// Handle Tutorial Toggle
	const handleTutorialToggle = async (value) => {
		if (!currentUserData?.email) return;

		try {
			// 
			const userDoc = doc(db, "users", currentUserData.email);
			await updateDoc(userDoc, { 
				showTutorial: value,
				// Reset closed tutorials when enabling tutorials
				completedTutorials: value ? {} : (await getDoc(userDoc)).data()?.completedTutorials || {}
			});
			setIsTutorialEnabled(value);
			setShowTutorial(value);
		} catch (error) {
			
			// Revert the toggle if there's an error
			setIsTutorialEnabled(!value);
			setShowTutorial(!value);
		}
	};

	const tutorialSteps = {
		friends: "Connect with friends and manage your friend requests here",
		settings: "Customize your account settings and tutorial preferences"
	};

	const totalTutorials = Object.keys(tutorialSteps).length;

	useEffect(() => {
		const fetchTutorialPreference = async () => {
			if (!currentUserData?.email) return;

			try {
				const initialValue = await initializeTutorialSetting(currentUserData.email);
				// 
				setIsTutorialEnabled(initialValue);
				setShowTutorial(initialValue);
			} catch (error) {
				
				// Default to true if there's an error
				setIsTutorialEnabled(true);
				setShowTutorial(true);
			}
		};

		fetchTutorialPreference();
	}, [currentUserData]);

	const handleTutorialActivation = (popupId) => {
		setActivePopupId(popupId); // Set the active popup ID
	};

	return (
		<Animated.View style={[headerBarStyle, { transform: [{ translateY: propHeaderTranslateY !== undefined ? propHeaderTranslateY : internalTranslateY }] }]}>
			{/* Tablet/Desktop: Logo on left */}
			{(!isMobile) && (
				currentUserData ?
					<Pressable style={styles.logoArea} onPress={() => handleLinkPress("Hub")}> 
						<Text style={styles.logoText}>MYTASSEL</Text>
					</Pressable>
					:
					<Pressable style={styles.logoArea} onPress={() => handleLinkPress("My Tassel - Welcome", { popupType: "login" })}>
						<Text style={styles.logoText}>MYTASSEL</Text>
					</Pressable>
			)}

			{/* Mobile: logo and hamburger */}
			{isMobile ? (
				<>
					{currentUserData ?
						<Pressable style={styles.logoArea} onPress={() => handleLinkPress("Hub")}> 
							<Text style={styles.logoText}>MYTASSEL</Text>
						</Pressable>
						:
						<Pressable style={styles.logoArea} onPress={() => navigation.navigate("My Tassel - Welcome", { popupType: "login" })}>
							<Text style={styles.logoText}>MYTASSEL</Text>
						</Pressable>
					}
					<View style={styles.rightGroupMobile}>
						{hasAdminAccess && (
							<TouchableOpacity 
								onPress={handleAdminPress}
								onMouseEnter={() => setHoveredIcon('admin')}
								onMouseLeave={() => setHoveredIcon(null)}
								style={[
									styles.iconTouchable,
									hoveredIcon === 'admin' && styles.iconTouchableHover
								]}
							>
								<FontAwesome5 name="shield-alt" size={14} color="#fefde2" />
							</TouchableOpacity>
						)}
						<TouchableOpacity 
							onPress={() => setShowSideMenu(true)}
							onMouseEnter={() => setHoveredIcon('menu')}
							onMouseLeave={() => setHoveredIcon(null)}
							style={[
								styles.iconTouchable,
								hoveredIcon === 'menu' && styles.iconTouchableHover
							]}
						>
							<FontAwesome5 name="bars" size={16} color="#fefde2" />
						</TouchableOpacity>
					</View>
				</>
			) : (
				// Tablet/Desktop: right group at far right
				<View style={styles.rightGroup}>
					{currentUserData && !isHubPage ?
						<Pressable 
							{...spotlightTarget(SPOTLIGHT_TARGETS.HEADER_GO_HOME)}
							style={[
								styles.backButton,
								hoveredButton === 'backDesktop' && styles.backButtonHover
							]} 
							onMouseEnter={() => setHoveredButton('backDesktop')}
							onMouseLeave={() => setHoveredButton(null)}
							onPress={() => handleLinkPress("Hub")}
						> 
							<Text style={styles.backButtonText}>{`Go Home`}</Text>
						</Pressable>
						:
						currentUserData ? null : (
						<Pressable 
							style={[
								styles.backButton,
								hoveredButton === 'backDesktop' && styles.backButtonHover
							]} 
							onMouseEnter={() => setHoveredButton('backDesktop')}
							onMouseLeave={() => setHoveredButton(null)}
							onPress={() => navigation.navigate("My Tassel - Welcome", { popupType: "login" })}
						>
							<Text style={styles.backButtonText}>{`Sign Up`}</Text>
						</Pressable>
						)
					}
					{renderLoginOrLogoutButton()}
					{hasAdminAccess && (
						<TouchableOpacity 
							onPress={handleAdminPress}
							onMouseEnter={() => setHoveredIcon('admin')}
							onMouseLeave={() => setHoveredIcon(null)}
							style={[
								styles.iconTouchable,
								hoveredIcon === 'admin' && styles.iconTouchableHover
							]}
						>
							<FontAwesome5 name="shield-alt" size={isMobile ? 14 : isTablet ? 18 : isSmallDesktop ? 20 : 24} color="#fefde2" />
						</TouchableOpacity>
					)}
					<TouchableOpacity 
						{...spotlightTarget(SPOTLIGHT_TARGETS.HEADER_FRIENDS)}
						onPress={() => {
							if (currentUserData) {
								setShowFriends(true)
							}
							else {
								setShowPromptSignUp(true)
							}
						}}
						onMouseEnter={() => setHoveredIcon('friends')}
						onMouseLeave={() => setHoveredIcon(null)}
						style={[
							styles.iconTouchable,
							hoveredIcon === 'friends' && styles.iconTouchableHover
						]}
					>
						<FontAwesome5 name="user-friends" size={isMobile ? 12 : isTablet ? 16 : isSmallDesktop ? 20 : 24} color="#fefde2" />
						{friendRequests.length > 0 && <View style={styles.notificationDot} />}
					</TouchableOpacity>
					<TouchableOpacity 
						{...spotlightTarget(SPOTLIGHT_TARGETS.HEADER_SETTINGS)}
						onPress={() => {
							setShowSettingsModal(true);
							fetchPrivacySetting();
						}}
						onMouseEnter={() => setHoveredIcon('settings')}
						onMouseLeave={() => setHoveredIcon(null)}
						style={[
							styles.iconTouchable,
							hoveredIcon === 'settings' && styles.iconTouchableHover
						]}
					>
						<FontAwesome5 name="cog" size={isMobile ? 16 : isTablet ? 20 : isSmallDesktop ? 24 : 28} color="#fefde2" />
					</TouchableOpacity>
				</View>
			)}

			{currentUserData ? (
				<Friends
					isVisible={showFriends}
					onClose={() => setShowFriends(false)}
					ref={friendsRef}
					isFriendsListPrivate={isPrivacyPublic} // Pass the state as a prop
					onPrivacyToggle={(privacy) => setIsPrivacyPublic(!privacy)}
					showPrivacyWarning={showPrivacyWarning}
					setShowPrivacyWarning={setShowPrivacyWarning}
					confirmPrivacyToggle={confirmPrivacyToggle}
				/>
			) : (
				<PromptSignUp isVisible={showPromptSignUp} onClose={() => setShowPromptSignUp(false)} redirectPage="Friends" />
			)}
			<PromptSignUp isVisible={showPromptSignUp} onClose={() => setShowPromptSignUp(false)} redirectPage="Profile" />

			{/* Sign Out Confirmation */}
			{showLogoutModal && (
				<View style={styles.blurredContainer}>
					<Modal transparent={true} animationType="slide" visible={showLogoutModal} onRequestClose={() => setShowLogoutModal(false)}>
						<View style={styles.overlayBlur} />
						<View style={styles.centeredView}>
							<View style={styles.modalContainer}>
								<View style={styles.modalContent}>
									<Text style={styles.modalTitle}>Are you sure you want to logout?</Text>
									<View style={styles.modalButtonContainer}>
										<Pressable
											style={styles.modalButton}
											onPress={() => {
												handleLogout();
												setShowLogoutModal(false);
											}}>
											<Text style={styles.modalButtonText}>Sign Out</Text>
										</Pressable>
										<Pressable
											style={[styles.modalButton, styles.cancelButton]}
											onPress={() => setShowLogoutModal(false)}>
											<Text style={styles.modalButtonText}>Cancel</Text>
										</Pressable>
									</View>
								</View>
							</View>
						</View>
					</Modal>
				</View>
			)}

			{/* Settings Modal */}
			{showSettingsModal && (
				<View style={styles.blurredContainer}>
					<Modal transparent={true} animationType="slide" visible={showSettingsModal} onRequestClose={() => setShowSettingsModal(false)}>
						<View style={styles.overlayBlur} />
						<View style={styles.centeredView}>
							<View style={styles.modalContainer}>
								<View style={styles.modalContent} {...spotlightTarget(SPOTLIGHT_TARGETS.SETTINGS_MODAL)}>
									<Text style={styles.modalTitle}>Settings</Text>

									{ currentUserData?.accountType == "Graduate" && (
										<View style={styles.settingItem} {...spotlightTarget(SPOTLIGHT_TARGETS.SETTINGS_PRIVACY)}>
											<Text style={styles.settingText}>Account Privacy </Text>
											<Switch value={!isPrivacyPublic} onValueChange={handlePrivacyToggle} />
										</View>
									)}

									<View style={styles.settingItem} {...spotlightTarget(SPOTLIGHT_TARGETS.SETTINGS_SHOW_TUTORIALS)}>
										<Text style={styles.settingText}>
											Show Tutorials
											</Text>
										<Switch 
											value={isTutorialEnabled}
											onValueChange={handleTutorialToggle}
										/>
									</View>
									<Pressable {...spotlightTarget(SPOTLIGHT_TARGETS.SETTINGS_CLOSE)} style={styles.closeModalButton} onPress={() => setShowSettingsModal(false)}>
										<Text style={styles.modalButtonText}>Close</Text>
									</Pressable>
								</View>
							</View>
						</View>
					</Modal>
				</View>
			)}

			{/* Privacy Warning Modal */}
			{showPrivacyWarning && (
				<View style={styles.blurredContainer}>
					<Modal transparent={true} animationType="slide" visible={showPrivacyWarning} onRequestClose={() => setShowPrivacyWarning(false)}>
						<View style={styles.overlayBlur} />
						<View style={styles.centeredView}>
							<View style={styles.modalContainer}>
								<View style={styles.modalContent}>
									<Text style={styles.modalTitle}>Privacy Warning</Text>
									<Text style={styles.warningText}>Making your page public will share your info with anyone, including your address if you create a registry.</Text>
									<Pressable style={styles.modalButton} onPress={confirmPrivacyToggle}>
										<Text style={styles.modalButtonText}>I Understand</Text>
									</Pressable>
									<Pressable style={styles.closeModalButton} onPress={() => setShowPrivacyWarning(false)}>
										<Text style={styles.modalButtonText}>Cancel</Text>
									</Pressable>
								</View>
							</View>
						</View>
					</Modal>
				</View>
			)}

			{/* Side Menu Modal */}
			{showSideMenu && (
				<Modal transparent={true} animationType="slide" visible={showSideMenu} onRequestClose={() => setShowSideMenu(false)}>
					<View style={styles.sideMenuOverlay}>
						<TouchableOpacity style={styles.sideMenuBackdrop} onPress={() => setShowSideMenu(false)} />
						<View style={styles.sideMenuContainer}>
							<View style={styles.sideMenuHeader}>
								<Text style={styles.sideMenuTitle}>Menu</Text>
								<TouchableOpacity onPress={() => setShowSideMenu(false)}>
									<FontAwesome5 name="times" size={20} color="#fefde2" />
								</TouchableOpacity>
							</View>
							<View style={styles.sideMenuContent}>
								<TouchableOpacity 
									style={styles.sideMenuItem}
									onPress={() => {
										setShowSideMenu(false);
										handleLinkPress("Hub");
									}}
								>
									<FontAwesome5 name="home" size={20} color="#fefde2" />
									<Text style={styles.sideMenuItemText}>Home</Text>
								</TouchableOpacity>
								<TouchableOpacity 
									style={styles.sideMenuItem}
									onPress={() => {
										setShowSideMenu(false);
										if (currentUserData) {
											setShowFriends(true);
										} else {
											setShowPromptSignUp(true);
										}
									}}
								>
									<FontAwesome5 name="user-friends" size={20} color="#fefde2" />
									<Text style={styles.sideMenuItemText}>Friends</Text>
									{friendRequests.length > 0 && <View style={styles.notificationDot} />}
								</TouchableOpacity>
								<TouchableOpacity 
									style={styles.sideMenuItem}
									onPress={() => {
										setShowSideMenu(false);
										setShowSettingsModal(true);
										fetchPrivacySetting();
									}}
								>
									<FontAwesome5 name="cog" size={20} color="#fefde2" />
									<Text style={styles.sideMenuItemText}>Settings</Text>
								</TouchableOpacity>
								{currentUserData && (
									<TouchableOpacity 
										style={styles.sideMenuItem}
										onPress={() => {
											setShowSideMenu(false);
											setShowLogoutModal(true);
										}}
									>
										<FontAwesome5 name="sign-out-alt" size={20} color="#fefde2" />
										<Text style={styles.sideMenuItemText}>Logout</Text>
									</TouchableOpacity>
								)}
							</View>
						</View>
					</View>
				</Modal>
			)}

			{/* Only render tutorials after loading and if they're in tutorialsToShow */}
			{!isLoading && showTutorial && (
				<>
					{tutorialsToShow.includes('friends') && (
						<TutorialPopup
							id="friends"
							steps={tutorialSteps.friends}
							position={{
								top: isSmallDesktop ? 55 : 55,
								right: isSmallDesktop ? 140 : 130
							}}
							title="Friends"
							openDirection="bottomLeft"
							totalTutorials={tutorialsToShow.length}
							style={{ zIndex: 100010 }}
							onHover={() => handleHover('friends')}
						/>
					)}

					{tutorialsToShow.includes('settings') && (
						<TutorialPopup
							id="settings"
							steps={tutorialSteps.settings}
							position={{ 
								top: isSmallDesktop ? 55 : 55, 
								right: isSmallDesktop ? 80 : 70
							}}
							title="Settings"
							openDirection="bottomLeft"
							totalTutorials={tutorialsToShow.length}
							style={{ zIndex: 100010 }}
							onHover={() => handleHover('settings')}
						/>
					)}
				</>
			)}
		</Animated.View>
	);
};

const styles = {
	logoArea: {
		flex: 1,
		flexDirection: "row",
		justifyContent: "space-between",
		backgroundColor: "transparent",
		maxHeight: isMobile ? 30 : isTablet ? 40 : isSmallDesktop ? 50 : 60,
	},
	rightGroup: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 12,
	},
	rightGroupMobile: {
		flexDirection: 'row',
		alignItems: 'center',
		marginLeft: 'auto',
		gap: 12,
	},
	loginArea: {
		flex: 0.5,
		flexDirection: "row",
		backgroundColor: "transparent",
		maxHeight: isMobile ? 30 : isTablet ? 40 : isSmallDesktop ? 50 : 60,
		gap: 16,
	},
	loginButton: {
		width: isMobile ? 100 : isTablet ? 110 : isSmallDesktop ? 130 : 150,
		height: isMobile ? 38 : isTablet ? 42 : isSmallDesktop ? 44 : 48,
		backgroundColor: isMobile ? "#4c956c" : "rgba(255, 255, 255, 0.08)",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: 24,
		borderWidth: 1,
		borderColor: isMobile ? "rgba(255, 255, 255, 0.08)" : "rgba(254, 253, 226, 0.25)",
		...(Platform.OS === 'web' ? { transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)", backdropFilter: "blur(8px)" } : {}),
		alignSelf: "center",
	},
	backButtonHover: {
		transform: "scale(1.05) translateY(-1px)",
		backgroundColor: "#3d8059",
		boxShadow: "0 6px 12px rgba(26, 92, 58, 0.35)",
	},
	loginButtonHover: {
		transform: "scale(1.05)",
		backgroundColor: isMobile ? "#3d8059" : "rgba(255, 255, 255, 0.15)",
		borderColor: "rgba(254, 253, 226, 0.4)",
	},
	backButton: {
		width: isMobile ? 110 : isTablet ? 120 : isSmallDesktop ? 140 : 160,
		height: isMobile ? 38 : isTablet ? 42 : isSmallDesktop ? 44 : 48,
		backgroundColor: "#4c956c",
		boxShadow: "0 6px 12px rgba(26, 92, 58, 0.25)",
		elevation: 4,
		alignItems: "center",
		justifyContent: "center",
		marginRight: isMobile ? 8 : 0,
		borderRadius: 24,
		transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
		minWidth: isTablet ? 120 : undefined,
		whiteSpace: 'nowrap',
		alignSelf: "center",
		borderWidth: 1,
		borderColor: "rgba(255, 255, 255, 0.08)",
	},
	backButtonText: {
		fontFamily: "Orpheus Pro",
		fontWeight: "600",
		fontSize: isMobile ? 14 : isTablet ? 15 : isSmallDesktop ? 16 : 18,
		lineHeight: isMobile ? 18 : isTablet ? 20 : isSmallDesktop ? 22 : 24,
		letterSpacing: 0.3,
		textAlign: "center",
		color: "#fefde2",
		textShadow: '1px 1px 2px rgba(0, 0, 0, 0.12)',
	},
	loginButtonText: {
		fontFamily: "Orpheus Pro",
		fontWeight: "600",
		fontSize: isMobile ? 14 : isTablet ? 15 : isSmallDesktop ? 16 : 18,
		lineHeight: isMobile ? 18 : 24,
		letterSpacing: 0.3,
		textAlign: "center",
		color: "#fefde2",
		textShadow: '1px 1px 2px rgba(0, 0, 0, 0.12)',
	},
	cancelButtonText: {
		fontFamily: "Orpheus Pro",
		fontSize: isMobile ? 20 : isTablet ? 25 : isSmallDesktop ? 28 : 34,
		textAlign: "center",
		color: "#e4e4e4",
		textShadow: '3px 3px 6px rgba(0, 0, 0, 0.09)',
		marginHorizontal: 25,
		paddingRight: 90
	},
	signOutButtonText: {
		fontFamily: "Orpheus Pro",
		fontSize: isMobile ? 20 : isTablet ? 25 : isSmallDesktop ? 28 : 34,
		textAlign: "center",
		color: "red",
		textShadow: '3px 3px 6px rgba(0, 0, 0, 0.09)',
	},
	logoText: {
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		fontWeight: "normal",
		fontSize: isMobile ? 20 : isTablet ? 40 : isSmallDesktop ? 50 : 50,
		textAlign: "left",
		color: "#fefde2",
		textShadow: '3px 3px 6px rgba(0, 0, 0, 0.09)',
		alignSelf: "center",
		lineHeight: isMobile ? 24 : isTablet ? 40 : isSmallDesktop ? 50 : 50,
	},
	blurredContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1000,
	},
	overlayBlur: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backdropFilter: "blur(25px)", // Adjust the px value to control the amount of blur
		zIndex: 1,
	},
	centeredView: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 2, // Ensure it appears above the blur overlay
	},
	modalContainer: {
		width: '80%', // Make the container bigger
		padding: 20,
		backgroundColor: "#fefde2",
		borderRadius: 10,
		alignItems: "center",
		boxShadow: "0 4px 8px rgba(0, 0, 0, 0.25)",
		elevation: 5,
	},
	modalContent: {
		alignItems: 'center',
	},
	modalTitle: {
		fontSize: 24,
		fontWeight: 'bold',
		marginBottom: 20,
		fontFamily: fontFamilies.Bodoni72Smallcaps,
	},
	modalButtonContainer: {
		width: '100%',
	},
	modalButton: {
		backgroundColor: "#4c956c",
		padding: 15,
		borderRadius: 5,
		marginTop: 10,
		width: "100%",
		alignItems: "center",
	},
	cancelButton: {
		backgroundColor: "red",
	},
	modalButtonText: {
		fontSize: 16,
		color: "white",
		fontFamily: fontFamilies.Bodoni72Smallcaps,
	},
	settingItem: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 20,
	},
	settingText: {
		fontSize: 16,
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		paddingRight: 10,
	},
	settingDescription: {
		fontSize: 14,
		color: "#333",
		marginTop: 10,
		textAlign: "center",
		fontFamily: fontFamilies.Bodoni72Smallcaps,
	},
	closeModalButton: {
		backgroundColor: "red",
		padding: 10,
		borderRadius: 5,
		marginTop: 10,
		width: "100%",
		alignItems: "center",
	},
	warningText: {
		fontSize: 14,
		marginBottom: 20,
		fontFamily: fontFamilies.Bodoni72Smallcaps,
	},
	iconRow: {
		flexDirection: 'row',
		alignItems: 'center',
		marginLeft: isMobile ? 16 : 24,
		gap: isMobile ? 12 : 16,
	},
	icon: {
		width: isMobile ? 24 : isTablet ? 32 : isSmallDesktop ? 36 : 40,
		height: isMobile ? 24 : isTablet ? 32 : isSmallDesktop ? 36 : 40,
		opacity: 0.8,
		transition: "all 0.2s ease",
	},
	iconHover: {
		opacity: 1,
		transform: "scale(1.1)",
	},
	iconTouchable: {
		width: isMobile ? 32 : isTablet ? 36 : isSmallDesktop ? 40 : 44,
		height: isMobile ? 32 : isTablet ? 36 : isSmallDesktop ? 40 : 44,
		backgroundColor: "rgba(255, 255, 255, 0.08)",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: 24,
		borderWidth: 1,
		borderColor: "rgba(254, 253, 226, 0.25)",
		...(Platform.OS === 'web' ? { transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)", backdropFilter: "blur(8px)" } : {}),
	},
	iconTouchableHover: {
		transform: "scale(1.05)",
		backgroundColor: "rgba(255, 255, 255, 0.15)",
		borderColor: "rgba(254, 253, 226, 0.4)",
	},
	notificationDot: {
		position: 'absolute',
		top: 0,
		right: 0,
		width: 8,
		height: 8,
		borderRadius: 4,
		backgroundColor: 'red',
	},
	settingsIcon: {
		// Removed marginLeft as we're using gap in iconRow now
	},
	leftGroup: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
	},
	sideMenuOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		flexDirection: 'row',
		justifyContent: 'flex-end',
		zIndex: 1000,
	},
	sideMenuBackdrop: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
	},
	sideMenuContainer: {
		width: 250,
		backgroundColor: '#4c956c',
		paddingTop: 50,
		paddingHorizontal: 20,
		paddingBottom: 20,
	},
	sideMenuHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 20,
	},
	sideMenuTitle: {
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		fontSize: 24,
		color: '#fefde2',
	},
	sideMenuContent: {
		flex: 1,
	},
	sideMenuItem: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 15,
		paddingHorizontal: 10,
		borderRadius: 8,
		marginBottom: 10,
		backgroundColor: 'rgba(255, 255, 255, 0.1)',
	},
	sideMenuItemText: {
		fontFamily: 'Orpheus Pro',
		fontSize: 18,
		color: '#fefde2',
		marginLeft: 15,
		flex: 1,
	},
};

export { useScrollHide };
export default HeaderBar;
